import { useEffect, useMemo, useState } from 'react';
import { Card, Title, Text, Flex, BarList, Dialog, DialogPanel } from '@tremor/react';
import { RangeSelectChart } from '../components/RangeSelectChart';
import { ErrorBanner } from '../components/ErrorBanner';
import {
  useOutlookPerfSummary,
  useOutlookPerfTimeseries,
  useOutlookPerfFetchBreakdown,
  useOutlookPerfTopSlow,
  useOutlookPerfPerRequest,
  useOutlookPerfBySentence,
  useOutlookPerfByClassifier,
  useOutlookPerfHedge,
  useOutlookPerfRequestDetail,
  useOutlookPerfLookup,
  type OutlookPerfParams,
} from '../hooks/useOutlookPerfStats';
import type { OutlookPerfRequestDetail } from '../types';

type Preset = '1h' | '24h' | '7d' | '30d' | 'custom';
type Granularity = 'auto' | 'hour' | 'day';

const PRESETS: { value: Preset; label: string }[] = [
  { value: '1h', label: 'Last 1h' },
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
  { value: 'custom', label: 'Custom' },
];

const PRESET_MS: Record<Exclude<Preset, 'custom'>, number> = {
  '1h': 3600_000,
  '24h': 86_400_000,
  '7d': 7 * 86_400_000,
  '30d': 30 * 86_400_000,
};

function fmtNumber(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}

function fmtPercent(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

function fmtMs(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(2)}s`;
  return `${Math.round(n)}ms`;
}

function fmtBucket(iso: string, granularity: 'hour' | 'day'): string {
  const d = new Date(iso);
  if (granularity === 'hour') {
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  }
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function fmtShortId(id: string | null): string {
  if (!id) return '—';
  return id.length > 8 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

export function OutlookMonitoringPage() {
  const [preset, setPreset] = useState<Preset>('24h');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [granularity, setGranularity] = useState<Granularity>('auto');
  const [orgId, setOrgId] = useState('');
  const [userId, setUserId] = useState('');

  const params = useMemo<(OutlookPerfParams & { granularity?: Granularity }) | null>(() => {
    let fromIso: string;
    let toIso: string;
    if (preset === 'custom') {
      if (!from || !to) return null;
      fromIso = new Date(from).toISOString();
      toIso = new Date(to).toISOString();
    } else {
      const now = Date.now();
      toIso = new Date(now).toISOString();
      fromIso = new Date(now - PRESET_MS[preset]).toISOString();
    }
    return {
      from: fromIso,
      to: toIso,
      granularity,
      orgId: orgId.trim() || undefined,
      userId: userId.trim() || undefined,
    };
  }, [preset, from, to, granularity, orgId, userId]);

  const summary = useOutlookPerfSummary(params);
  const timeseries = useOutlookPerfTimeseries(params);
  const fetchBreakdown = useOutlookPerfFetchBreakdown(params);
  const topSlow = useOutlookPerfTopSlow(params ? { ...params, limit: 20 } : null);
  const perRequest = useOutlookPerfPerRequest(params);
  const bySentence = useOutlookPerfBySentence(params);
  const byClassifier = useOutlookPerfByClassifier(params);
  const hedge = useOutlookPerfHedge(params);
  const lookup = useOutlookPerfLookup(orgId.trim() || undefined, userId.trim() || undefined);

  // Drill-down modal state — fetched on demand for the clicked row.
  const [detailId, setDetailId] = useState<string | null>(null);

  const notConfigured = isNotConfiguredError(summary.error)
    || isNotConfiguredError(timeseries.error)
    || isNotConfiguredError(fetchBreakdown.error)
    || isNotConfiguredError(topSlow.error)
    || isNotConfiguredError(perRequest.error)
    || isNotConfiguredError(bySentence.error)
    || isNotConfiguredError(byClassifier.error)
    || isNotConfiguredError(hedge.error);

  // Join "all attempts" stats (from fetchBreakdown) and "winner only" stats
  // (from hedge.byName) on `name` so we can render both side by side. The
  // gap between them is exactly the inflation caused by losing hedge attempts.
  const fetchBreakdownJoined = useMemo(() => {
    if (!fetchBreakdown.data) return [];
    const winnersByName = new Map(
      (hedge.data?.byName ?? []).map((r) => [r.name, r]),
    );
    return fetchBreakdown.data.byName.map((r) => ({
      ...r,
      winner: winnersByName.get(r.name) ?? null,
    }));
  }, [fetchBreakdown.data, hedge.data]);

  const granularityResolved = timeseries.data?.granularity ?? 'hour';

  const seriesData = useMemo(() => {
    if (!timeseries.data) return [];
    return timeseries.data.series.map((b) => ({
      bucket: fmtBucket(b.bucket, granularityResolved),
      apiP95: b.apiP95 ?? null,
      reflectionP95: b.reflectionP95 ?? null,
      fetchP95: b.fetchP95 ?? null,
      fetchWinnerP95: b.fetchWinnerP95 ?? null,
      apiCount: b.apiCount ?? 0,
      reflectionCount: b.reflectionCount ?? 0,
      fetchCount: b.fetchCount ?? 0,
    }));
  }, [timeseries.data, granularityResolved]);

  // Zoom state — controlled brush window shared across the latency chart
  // (which renders the brush) and the per-layer volume charts (which slice
  // their data to the brush window). Reset whenever the user picks a new
  // preset / range / filter so the brush always starts wide.
  const [zoomRange, setZoomRange] = useState<{ start: number; end: number } | null>(null);
  useEffect(() => {
    setZoomRange(null);
  }, [preset, from, to, granularity, orgId, userId]);

  const fullLen = seriesData.length;
  const zoomStart = zoomRange ? Math.max(0, Math.min(zoomRange.start, fullLen - 1)) : 0;
  const zoomEnd = zoomRange ? Math.max(zoomStart, Math.min(zoomRange.end, fullLen - 1)) : Math.max(0, fullLen - 1);
  const isZoomed = zoomRange !== null && (zoomStart > 0 || zoomEnd < fullLen - 1);

  const slicedSeries = useMemo(() => {
    if (fullLen === 0) return [];
    return seriesData.slice(zoomStart, zoomEnd + 1);
  }, [seriesData, zoomStart, zoomEnd, fullLen]);

  const handleZoom = (start: number, end: number) => {
    if (start === 0 && end === fullLen - 1) {
      setZoomRange(null);
    } else {
      setZoomRange({ start, end });
    }
  };
  const resetZoom = () => setZoomRange(null);

  return (
    <div className="space-y-6">
      {/* Header / controls */}
      <Card className="bg-gray-900 ring-gray-800">
        <Flex justifyContent="between" alignItems="center" className="flex-wrap gap-3">
          <div>
            <Title className="text-gray-100">Outlook HarmCheck — request-path performance</Title>
            <Text className="text-gray-500 text-xs mt-1 max-w-3xl">
              Diagnoses "why is Outlook HarmCheck slow?" by correlating three layers from{' '}
              <span className="font-mono">alphy-reflect-web</span>:
              <span className="text-cyan-400"> ApiRequest</span> (one row per Outlook API call, end-to-end) →
              <span className="text-violet-400"> ReflectionLog</span> (one row <i>per sentence classified</i>, so each Outlook request fans out into N rows) →
              <span className="text-amber-400"> FetchLog</span> (one row <i>per upstream attempt</i> — and hedge / speculative retries mean one logical call can produce multiple rows).
              Cards below progress from "is the system fast" → "is one request fast" → "is one upstream call fast".
            </Text>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as Preset)}
              className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-sm rounded px-2 py-1"
            >
              {PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            {preset === 'custom' && (
              <>
                <input
                  type="datetime-local"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-sm rounded px-2 py-1"
                />
                <span className="text-gray-500 text-sm">→</span>
                <input
                  type="datetime-local"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-sm rounded px-2 py-1"
                />
              </>
            )}
            <div className="flex gap-1 rounded-md ring-1 ring-gray-800 p-1">
              {(['auto', 'hour', 'day'] as Granularity[]).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGranularity(g)}
                  className={`px-2 py-0.5 text-xs rounded ${
                    granularity === g ? 'bg-gray-700 text-gray-100' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </Flex>
        <div className="mt-3 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              placeholder="Org filter — ObjectId, domain, or ownerEmail"
              title="Type an org's ObjectId (24 hex chars), domain (e.g. acme.com), or ownerEmail (e.g. admin@acme.com). Resolved against alphy-reflect-web's organizations collection."
              className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-xs rounded px-2 py-1 w-72 font-mono"
            />
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="User filter — ObjectId or email"
              title="Type a user's ObjectId (24 hex chars) or email (e.g. jane@acme.com). Resolved against alphy-reflect-web's users collection."
              className="bg-gray-800 ring-1 ring-gray-700 text-gray-100 text-xs rounded px-2 py-1 w-72 font-mono"
            />
          </div>
          <FilterLookupHint orgInput={orgId.trim()} userInput={userId.trim()} lookup={lookup.data} />
        </div>
        {summary.data && (
          <Text className="text-gray-500 text-xs mt-2">
            {new Date(summary.data.range.from).toLocaleString()} →{' '}
            {new Date(summary.data.range.to).toLocaleString()} · granularity {granularityResolved}
          </Text>
        )}
      </Card>

      {notConfigured && <NotConfiguredCard />}

      {!notConfigured && (
        <>
          {/* KPI strip — PER-ROW percentiles. The hints below remind you that
              reflection p95 is per-sentence and fetch p95 is per-attempt, so
              they're not directly comparable to end-to-end. The per-request
              rollup card later in the page is the apples-to-apples view. */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi
              label="API requests"
              value={fmtNumber(summary.data?.apiRequest?.count)}
              hint={`avg ${fmtMs(summary.data?.apiRequest?.avgMs)} end-to-end`}
            />
            <Kpi
              label="p95 end-to-end"
              value={fmtMs(summary.data?.apiRequest?.p95)}
              hint={`per request · p50 ${fmtMs(summary.data?.apiRequest?.p50)} · p99 ${fmtMs(summary.data?.apiRequest?.p99)}`}
            />
            <Kpi
              label="p95 per sentence"
              value={fmtMs(summary.data?.reflectionLog?.p95)}
              hint={`${fmtNumber(summary.data?.reflectionLog?.count)} sentences · NOT per request`}
            />
            <Kpi
              label="p95 per fetch attempt"
              value={fmtMs(summary.data?.fetchLog?.p95)}
              hint={`${fmtNumber(summary.data?.fetchLog?.count)} attempts · includes losing hedges`}
            />
            <Kpi
              label="Orchestration overhead"
              value={fmtMs(summary.data?.orchestrationOverhead?.p50)}
              hint={`median (p95 ${fmtMs(summary.data?.orchestrationOverhead?.p95)}) · API time − slowest sentence`}
            />
          </div>

          {/* Layer latency timeseries — owns the brush; all charts below
              follow the same zoom window via slicedSeries. */}
          <Card className="bg-gray-900 ring-gray-800">
            <Flex justifyContent="between" alignItems="center" className="mb-1">
              <Title className="text-gray-100">Layer latency over time (p95)</Title>
              {isZoomed && (
                <button
                  type="button"
                  onClick={resetZoom}
                  className="text-xs px-2 py-1 rounded ring-1 ring-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
                >
                  Reset zoom
                </button>
              )}
            </Flex>
            <Text className="text-gray-500 text-xs mb-2">
              When a layer's p95 rises above the layer below it, that's where time is being lost.
              If API p95 climbs but Reflection and Fetch stay flat → orchestration / network in alphy-reflect-web.
              If Fetch p95 climbs → upstream (HarmCheck Platform or OpenAI).
              <span className="block mt-1">
                <b>Fetch p95 (winner)</b> is the attempt that resolved the hedge race in absolute
                wall-clock time — the latency users actually waited for. Sourced from
                <span className="font-mono"> FetchLog.isWinner</span> stamped at write time, so
                it's index-cheap to query (no groupBy on the request path).{' '}
                <b>Fetch p95 (all attempts)</b> includes losing hedges — a spike here without a
                matching slow ApiRequest = an upstream call took forever but a hedge beat it;
                users weren't affected. Hidden by default — click the legend to toggle.{' '}
                <span className="text-gray-400">Drag the brush handles below to zoom.</span>
              </span>
            </Text>
            {timeseries.error && !timeseries.data ? (
              <ErrorBanner error={timeseries.error} />
            ) : timeseries.isLoading && !timeseries.data ? (
              <div className="py-8 text-center text-gray-500 text-sm">Loading…</div>
            ) : seriesData.length === 0 ? (
              <Text className="text-gray-500 text-sm">No activity in this range.</Text>
            ) : (
              <RangeSelectChart
                data={seriesData}
                xKey="bucket"
                series={[
                  { key: 'apiP95', label: 'API p95', color: 'cyan' },
                  { key: 'reflectionP95', label: 'Reflection p95', color: 'violet' },
                  { key: 'fetchWinnerP95', label: 'Fetch p95 (winner)', color: 'amber' },
                  { key: 'fetchP95', label: 'Fetch p95 (all attempts)', color: 'gray', defaultHidden: true },
                ]}
                valueFormatter={fmtMs}
                syncId="outlook-perf-ts"
                showBrush
                brushStart={zoomStart}
                brushEnd={zoomEnd}
                onBrushChange={handleZoom}
                heightClass="h-60"
              />
            )}
          </Card>

          {/* Volume by layer — three stacked sub-charts. Each gets its own
              y-axis because the scales differ wildly (a single ApiRequest
              fans out into N ReflectionLogs and N×attempts FetchLogs). All
              three follow the zoom window from the latency chart's brush. */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Volume by layer</Title>
            <Text className="text-gray-500 text-xs mb-3">
              Each ApiRequest fans out into many ReflectionLogs (one per sentence) and many
              FetchLogs (one per upstream call attempt). The three layers are stacked vertically
              so each gets its own y-scale — they share the time axis with the latency chart above.
            </Text>
            {timeseries.error && !timeseries.data ? (
              <ErrorBanner error={timeseries.error} />
            ) : seriesData.length === 0 ? (
              <Text className="text-gray-500 text-sm">No activity in this range.</Text>
            ) : (
              <div className="space-y-3">
                <VolumeSubChart
                  label="ApiRequests"
                  data={slicedSeries}
                  series={[{ key: 'apiCount', label: 'ApiRequests', color: 'cyan' }]}
                />
                <VolumeSubChart
                  label="ReflectionLogs"
                  data={slicedSeries}
                  series={[{ key: 'reflectionCount', label: 'ReflectionLogs', color: 'violet' }]}
                />
                <VolumeSubChart
                  label="FetchLogs"
                  data={slicedSeries}
                  series={[{ key: 'fetchCount', label: 'FetchLogs', color: 'amber' }]}
                />
              </div>
            )}
          </Card>

          {/* Per-request layer time — child rows rolled up to the parent
              ApiRequest, then percentiles taken across the window. */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Per-request layer time</Title>
            <Text className="text-gray-500 text-xs mb-3">
              For each ApiRequest, child ReflectionLog and FetchLog rows are summed (and max'd)
              up to the parent. <b>Total per request</b> = work done across all sentences;{' '}
              <b>Max per request</b> = the single slowest sentence / fetch in that request.
              Compare to <b>end-to-end</b> to see what's left over (orchestration / network in
              alphy-reflect-web). <b>Fetch sum</b> overcounts hedged retries — read it as
              "total upstream work", not wall time.
            </Text>
            <PerRequestTable data={perRequest.data} error={perRequest.error} isLoading={perRequest.isLoading} />
            <div className="mt-3 text-xs text-gray-500 space-y-1">
              <div>
                <b className="text-gray-400">Fan-out</b> — sentences per request: p50{' '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.sentenceCountPerRequest?.p50)}</span>
                {' · '}p95{' '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.sentenceCountPerRequest?.p95)}</span>
                {' · '}p99{' '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.sentenceCountPerRequest?.p99)}</span>
                {' · fetches per request: p50 '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.fetchCountPerRequest?.p50)}</span>
                {' · p95 '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.fetchCountPerRequest?.p95)}</span>
              </div>
              <div>
                <b className="text-gray-400">Client lag</b> (browser → server, from{' '}
                <span className="font-mono">x-client-start</span> header): p50{' '}
                <span className="text-gray-300">{fmtMs(perRequest.data?.requestLagPerRequest?.p50)}</span>
                {' · '}p95{' '}
                <span className="text-gray-300">{fmtMs(perRequest.data?.requestLagPerRequest?.p95)}</span>
                {' · '}p99{' '}
                <span className="text-gray-300">{fmtMs(perRequest.data?.requestLagPerRequest?.p99)}</span>
                . High client lag = network / wakeup, not backend.
              </div>
              <div>
                <b className="text-gray-400">Payload size</b> — bodyLength (chars): p50{' '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.bodyLengthPerRequest?.p50)}</span>
                {' · '}p95{' '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.bodyLengthPerRequest?.p95)}</span>
                {' · '}p99{' '}
                <span className="text-gray-300">{fmtNumber(perRequest.data?.bodyLengthPerRequest?.p99)}</span>
                . Larger bodies usually mean more sentences — cross-check with Latency by sentence count below.
              </div>
            </div>
          </Card>

          {/* Latency by sentence count — answers "do longer emails get slower?" */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Latency by sentence count</Title>
            <Text className="text-gray-500 text-xs mb-3">
              ApiRequests bucketed by number of sentences classified (the fan-out). p95 column is
              the headline — if it rises with bucket size, slowness is driven by long emails.
              "Per sentence" shows whether each sentence is also getting more expensive as N grows
              (concurrency / backpressure within a single request).
            </Text>
            {bySentence.error && !bySentence.data ? (
              <ErrorBanner error={bySentence.error} />
            ) : bySentence.data && bySentence.data.buckets.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-xs">
                    <tr>
                      <th className="text-left py-1 pr-3" title="Sentence count bucket — how many ReflectionLogs were written for the parent ApiRequest. Outlook's postOnSend splits an email body on '.' so this is roughly sentence count plus the subject line.">Sentences</th>
                      <th className="text-right py-1 px-3" title="Number of ApiRequests that landed in this sentence-count bucket.">Requests</th>
                      <th className="text-right py-1 px-3" title="50th percentile of ApiRequest.responseMs (end-to-end) for requests in this bucket.">p50 ms</th>
                      <th className="text-right py-1 px-3" title="95th percentile of ApiRequest.responseMs (end-to-end) for requests in this bucket. The headline column — if it climbs with sentence count, long emails drive slowness.">p95 ms</th>
                      <th className="text-right py-1 px-3" title="99th percentile of ApiRequest.responseMs (end-to-end) for requests in this bucket.">p99 ms</th>
                      <th className="text-right py-1 px-3" title="Mean ApiRequest.responseMs for requests in this bucket.">avg ms</th>
                      <th className="text-right py-1 px-3" title="Average per-sentence cost — total request time divided by total sentences across this bucket. If this rises with sentence count there's backpressure or serial work inside one request.">ms / sentence</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-200">
                    {bySentence.data.buckets.map((b) => (
                      <tr key={b.bucketKey} className="border-t border-gray-800">
                        <td className="py-1 pr-3 font-mono text-xs">{b.label}</td>
                        <td className="py-1 px-3 text-right">{fmtNumber(b.count)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(b.p50)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(b.p95)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(b.p99)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(b.avgMs)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(b.avgMsPerSentence)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Text className="text-gray-500 text-sm">No requests in this range.</Text>
            )}
          </Card>

          {/* Per-classifier reflection latency — uses the per-classifier
              duration columns on ReflectionLog (no need to join fetchlogs). */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Reflection time — by classifier</Title>
            <Text className="text-gray-500 text-xs mb-3">
              ReflectionLog stores a per-classifier wall-time column for each model that ran
              (e.g. <span className="font-mono">openai-hatesanpre</span>,{' '}
              <span className="font-mono">openai-sentiment</span>, <span className="font-mono">intimidation</span>,{' '}
              <span className="font-mono">ner</span>, <span className="font-mono">spacy</span>).
              Sorted by p95 — the top row is the per-classifier tall-pole.
            </Text>
            {byClassifier.error && !byClassifier.data ? (
              <ErrorBanner error={byClassifier.error} />
            ) : byClassifier.data && byClassifier.data.byClassifier.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-xs">
                    <tr>
                      <th className="text-left py-1 pr-3" title="The per-classifier latency column on ReflectionLog (e.g. 'openai-hatesanpre'). One column per active classifier.">Classifier</th>
                      <th className="text-right py-1 px-3" title="Number of ReflectionLog rows where this classifier ran (column is non-null and > 0).">Sentences classified</th>
                      <th className="text-right py-1 px-3" title="50th percentile of this classifier's wall time across the sentences it ran on.">p50</th>
                      <th className="text-right py-1 px-3" title="95th percentile — the headline column. Sorted by p95 descending; the top row is the tall pole for reflection time.">p95</th>
                      <th className="text-right py-1 px-3" title="99th percentile of this classifier's wall time.">p99</th>
                      <th className="text-right py-1 px-3" title="Mean wall time for this classifier.">avg</th>
                      <th className="text-right py-1 px-3" title="Worst single sentence-classifier observation.">max</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-200">
                    {byClassifier.data.byClassifier.map((r) => (
                      <tr key={r.classifier} className="border-t border-gray-800">
                        <td className="py-1 pr-3 font-mono text-xs">{r.classifier}</td>
                        <td className="py-1 px-3 text-right">{fmtNumber(r.count)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(r.p50)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(r.p95)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(r.p99)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(r.avgMs)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(r.maxMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Text className="text-gray-500 text-sm">No reflections in this range.</Text>
            )}
          </Card>

          {/* Fetch breakdown — both "all attempts" and "winner only" columns
              so the inflation from losing hedge attempts is visible. */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Upstream fetches — by name</Title>
            <Text className="text-gray-500 text-xs mb-3">
              Compares the HarmCheck Platform path (<span className="font-mono">platform-reflections</span>)
              against the legacy direct OpenAI path (<span className="font-mono">openai-*</span>).
              Each row is one upstream classifier endpoint.
              <span className="block mt-1">
                <b>Wall-time p95 (winner)</b> is what the user actually waited for — it considers only
                the hedge attempt that resolved first per logical call.
                <b> All-attempt p95</b> is the same number across <i>every</i> attempt, including losing
                hedges that finished after the winner. The gap between the two columns is exactly
                the inflation caused by speculative retries — useful to spot, but not user-facing.
              </span>
            </Text>
            {(fetchBreakdown.error || hedge.error) && !fetchBreakdown.data ? (
              <ErrorBanner error={fetchBreakdown.error || hedge.error} />
            ) : fetchBreakdown.data && fetchBreakdownJoined.length > 0 ? (
              <>
                <BarList
                  data={fetchBreakdownJoined.map((r) => ({
                    name: `${r.name} · wall p95 ${fmtMs(r.winner?.winnerP95 ?? null)} · all p95 ${fmtMs(r.p95)} · err ${fmtPercent(r.errorRate)}`,
                    value: r.count,
                  }))}
                  valueFormatter={fmtNumber}
                />
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-gray-500 text-xs">
                      <tr>
                        <th className="text-left py-1 pr-3" title="FetchLog.name — the upstream identifier. 'platform-reflections' = HarmCheck Platform path; 'openai-*' = legacy direct OpenAI per-classifier paths.">Name</th>
                        <th className="text-right py-1 px-3" title="Total successful attempts in the window (every row that wrote a FetchLog), including losing hedge attempts.">All attempts</th>
                        <th className="text-right py-1 px-3" title="Logical fetches — one per (reflection, classifier) tuple. Each may spawn 1, 2, or 3 attempts under the hedge race.">Logical fetches</th>
                        <th className="text-right py-1 px-3" title="50th percentile of latency considering ONLY the winning attempt per logical fetch — what the user actually waited for.">Wall p50</th>
                        <th className="text-right py-1 px-3" title="95th percentile of WINNER latency. Use this when you want user-perceived wall time.">Wall p95</th>
                        <th className="text-right py-1 px-3" title="50th percentile across every successful attempt (including hedge losers that finished after the race ended). Higher = more hedge contamination.">All p50</th>
                        <th className="text-right py-1 px-3" title="95th percentile across every successful attempt. Compare to Wall p95 — the gap is the hedge inflation.">All p95</th>
                        <th className="text-right py-1 px-3" title="Percent of attempts whose HTTP status was missing or >= 400. Errors and 5xx-driven retries both land here.">Err %</th>
                        <th className="text-right py-1 px-3" title="% of LOGICAL fetches where the hedge fired (attempts > 1). Target ~5% per the Tail-at-Scale rule.">Hedge fire %</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-200">
                      {fetchBreakdownJoined.map((r) => (
                        <tr key={r.name} className="border-t border-gray-800">
                          <td className="py-1 pr-3 font-mono text-xs">{r.name}</td>
                          <td className="py-1 px-3 text-right">{fmtNumber(r.count)}</td>
                          <td className="py-1 px-3 text-right">{fmtNumber(r.winner?.groups ?? null)}</td>
                          <td className="py-1 px-3 text-right">{fmtMs(r.winner?.winnerP50 ?? null)}</td>
                          <td className="py-1 px-3 text-right">{fmtMs(r.winner?.winnerP95 ?? null)}</td>
                          <td className="py-1 px-3 text-right text-gray-400">{fmtMs(r.p50)}</td>
                          <td className="py-1 px-3 text-right text-gray-400">{fmtMs(r.p95)}</td>
                          <td className="py-1 px-3 text-right">{fmtPercent(r.errorRate)}</td>
                          <td className="py-1 px-3 text-right">{fmtPercent(r.winner?.hedgeFireRate ?? null)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <Text className="text-gray-500 text-sm">No fetches in this range.</Text>
            )}
          </Card>

          {/* Hedge performance — answers "how well is the speculative-retry
              race actually working?" */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Hedge / speculative-retry performance</Title>
            <Text className="text-gray-500 text-xs mb-3">
              alphy-reflect-web fires every upstream call as a <b>tied request race</b>: the first
              attempt fires immediately, a second fires at +600ms and a third at +1800ms <i>only if</i>
              no winner has resolved yet. The first to complete wins; losing in-flight attempts are
              NOT cancelled (they finish later and still write FetchLog rows). Below: how often the
              hedge fires, who wins, and how much wall time it saves vs. attempt #1 alone.
            </Text>
            <HedgeSummary data={hedge.data} error={hedge.error} isLoading={hedge.isLoading} />
            {hedge.data && hedge.data.byName.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-xs">
                    <tr>
                      <th className="text-left py-1 pr-3" title="FetchLog.name — the upstream identifier.">Name</th>
                      <th className="text-right py-1 px-3" title="Logical fetches — one per (reflection, classifier) tuple. Each may spawn 1, 2, or 3 attempts under the hedge race.">Logical fetches</th>
                      <th className="text-right py-1 px-3" title="% of logical fetches where the hedge fired (attempts > 1).">Hedge fire %</th>
                      <th className="text-right py-1 px-3" title="Mean attempts per logical fetch. 1.00 = hedge never fires; closer to 2 or 3 = hedges fire frequently.">Avg attempts</th>
                      <th className="text-right py-1 px-3" title="% of logical fetches whose winner was attempt #1 (the immediate try). Higher is better — the upstream is fast enough that the hedge isn't needed.">Won by #1</th>
                      <th className="text-right py-1 px-3" title="% of logical fetches whose winner was attempt #2 (fired at +600ms). Hedge earned its keep.">Won by #2</th>
                      <th className="text-right py-1 px-3" title="% of logical fetches whose winner was attempt #3 (fired at +1800ms). Tail was bad enough that even #2 didn't help.">Won by #3</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-200">
                    {hedge.data.byName.map((r) => (
                      <tr key={r.name} className="border-t border-gray-800">
                        <td className="py-1 pr-3 font-mono text-xs">{r.name}</td>
                        <td className="py-1 px-3 text-right">{fmtNumber(r.groups)}</td>
                        <td className="py-1 px-3 text-right">{fmtPercent(r.hedgeFireRate)}</td>
                        <td className="py-1 px-3 text-right">{r.avgAttempts?.toFixed(2) ?? '—'}</td>
                        <td className="py-1 px-3 text-right">{fmtPercent(r.winnerByAttemptPct[1])}</td>
                        <td className="py-1 px-3 text-right">{fmtPercent(r.winnerByAttemptPct[2])}</td>
                        <td className="py-1 px-3 text-right">{fmtPercent(r.winnerByAttemptPct[3])}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Hedge tuning guidance — interprets the hedge perf data and
              suggests per-name delay tweaks. */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Hedge tuning guidance</Title>
            <Text className="text-gray-500 text-xs mb-3">
              <b>The tradeoff:</b> a shorter first delay catches the slow tail sooner (better p95 / p99
              for users) but fires hedges more often (more load on the upstream and your OpenAI /
              Platform bill). A longer first delay is the opposite. The classic Dean &amp; Barroso{' '}
              <i>Tail at Scale</i> rule of thumb: set the first hedge delay at roughly the{' '}
              <b>p95 of single-call latency</b> so the hedge fires ~5% of the time — enough to chop
              the tail, not so much that you're regularly doubling upstream work.
              <span className="block mt-1">
                <b>Where the delays live:</b>{' '}
                <span className="font-mono">alphy-reflect-web/helpers/fetchWithTimeout.js</span> lines 99–101
                — currently hardcoded at <span className="font-mono">600</span> /{' '}
                <span className="font-mono">1800</span> ms.
              </span>
            </Text>
            <HedgeTuningTable data={hedge.data} error={hedge.error} isLoading={hedge.isLoading} />
            <div className="mt-4 text-xs text-gray-500 space-y-1">
              <div><b className="text-gray-400">Read the table:</b></div>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <b className="text-emerald-400">Healthy</b> — fire rate is 2–10%, winnerP95 is well under
                  the first delay, and most wins come from attempt #1. Hedges are a safety net.
                </li>
                <li>
                  <b className="text-amber-400">Hedges fire too often</b> — fire rate &gt; 15%, attempt #2 or
                  #3 win a lot. You're paying for duplicate upstream work. Either raise <span className="font-mono">delay1</span> closer to current p95, OR investigate why the upstream is slow.
                </li>
                <li>
                  <b className="text-amber-400">Wasted hedges</b> — fire rate &gt; 5% but #2/#3 rarely win.
                  Hedges fire and lose. Raise <span className="font-mono">delay1</span> toward p99.
                </li>
                <li>
                  <b className="text-rose-400">Tail escapes the hedge</b> — winnerP99 &gt; <span className="font-mono">delay2</span> (1800ms).
                  Some requests fall through all three attempts and still take forever. Lower <span className="font-mono">delay2</span>, or accept that the upstream tail is the bottleneck.
                </li>
                <li>
                  <b className="text-gray-400">Why the "suggested" numbers:</b> we round winnerP95 up to
                  the nearest 50ms for <span className="font-mono">delay1</span> (so hedge #1 fires only for
                  the slow tail), and winnerP99 up to the nearest 100ms for{' '}
                  <span className="font-mono">delay2</span> (so hedge #2 catches the really bad cases).
                </li>
                <li>
                  These are starting points based purely on observed latency.
                  Validate by changing one delay, watching this card for a day, and confirming
                  fire rate and p95 winner move the right way.
                </li>
              </ul>
            </div>
          </Card>

          {/* Top slow requests */}
          <Card className="bg-gray-900 ring-gray-800">
            <Title className="text-gray-100">Slowest ApiRequests</Title>
            <Text className="text-gray-500 text-xs mb-3">
              Top 20 in window by end-to-end responseMs. <b>Click a row</b> to open the full
              breakdown for that request — every sentence's per-classifier latency and every
              upstream fetch attempt (with hedge winners highlighted).
            </Text>
            {topSlow.error && !topSlow.data ? (
              <ErrorBanner error={topSlow.error} />
            ) : topSlow.data && topSlow.data.requests.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 text-xs">
                    <tr>
                      <th className="text-left py-1 pr-3" title="ApiRequest.at — when the request landed at the server.">When</th>
                      <th className="text-left py-1 px-3" title="ApiRequest.endpoint — the HTTP path the Outlook add-in hit (e.g. /api/postOnSend).">Endpoint</th>
                      <th className="text-left py-1 px-3" title="Truncated org_id / user_id. Paste either into the filter inputs above (or the org's domain / user's email) to scope the whole dashboard.">Org / User</th>
                      <th className="text-right py-1 px-3" title="ApiRequest.responseMs — full server-side end-to-end duration. The user-perceived value.">End-to-end</th>
                      <th className="text-right py-1 px-3" title="Max ReflectionLog.responseMs across this request's sentences — the slowest single-sentence classification.">Max Refl ms</th>
                      <th className="text-right py-1 px-3" title="Count of ReflectionLog rows for this request = number of sentences classified.">Reflections</th>
                      <th className="text-right py-1 px-3" title="Count of FetchLog rows for this request — includes hedge attempts. Excludes canary fetches.">Fetches</th>
                      <th className="text-left py-1 px-3" title="The single FetchLog row with the highest responseMs for this request — name plus duration.">Slowest fetch</th>
                      <th className="text-left py-1 pl-3" title="The ApiRequest._id. Click any row to open the full breakdown for this id; or look it up in alphy-reflect-web logs directly.">apiRequestId</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-200">
                    {topSlow.data.requests.map((r) => (
                      <tr
                        key={r.apiRequestId}
                        onClick={() => setDetailId(r.apiRequestId)}
                        className="border-t border-gray-800 cursor-pointer hover:bg-gray-800/40"
                      >
                        <td className="py-1 pr-3 whitespace-nowrap">
                          {r.at ? new Date(r.at).toLocaleString() : '—'}
                        </td>
                        <td className="py-1 px-3 font-mono text-xs">{r.endpoint ?? '—'}</td>
                        <td className="py-1 px-3 font-mono text-xs">
                          {fmtShortId(r.orgId)} / {fmtShortId(r.userId)}
                        </td>
                        <td className="py-1 px-3 text-right">{fmtMs(r.responseMs)}</td>
                        <td className="py-1 px-3 text-right">{fmtMs(r.maxReflectionMs)}</td>
                        <td className="py-1 px-3 text-right">{fmtNumber(r.reflectionCount)}</td>
                        <td className="py-1 px-3 text-right">{fmtNumber(r.fetchCount)}</td>
                        <td className="py-1 px-3 font-mono text-xs">
                          {r.slowestFetch
                            ? `${r.slowestFetch.name ?? '—'} · ${fmtMs(r.slowestFetch.responseMs)}`
                            : '—'}
                        </td>
                        <td className="py-1 pl-3 font-mono text-[10px] text-gray-500">{r.apiRequestId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <Text className="text-gray-500 text-sm">No requests in this range.</Text>
            )}
          </Card>
        </>
      )}

      <RequestDetailModal
        apiRequestId={detailId}
        onClose={() => setDetailId(null)}
      />
    </div>
  );
}

function FilterLookupHint({
  orgInput,
  userInput,
  lookup,
}: {
  orgInput: string;
  userInput: string;
  lookup: import('../types').OutlookPerfLookupResponse | null;
}) {
  if (!orgInput && !userInput) return null;
  const orgResolved = lookup?.org;
  const userResolved = lookup?.user;
  const parts: React.ReactNode[] = [];
  if (orgInput) {
    parts.push(
      <span key="org">
        <span className="text-gray-400">Org:</span>{' '}
        {orgResolved ? (
          <span>
            <span className="text-emerald-400">{orgResolved.name ?? '(unnamed)'}</span>
            {orgResolved.domain && <span className="text-gray-400"> · {orgResolved.domain}</span>}
            <span className="text-gray-500"> → {orgResolved._id.slice(0, 8)}…</span>
          </span>
        ) : (
          <span className="text-amber-400">no match — filter ignored</span>
        )}
      </span>,
    );
  }
  if (userInput) {
    parts.push(
      <span key="user">
        <span className="text-gray-400">User:</span>{' '}
        {userResolved ? (
          <span>
            <span className="text-emerald-400">{userResolved.email ?? '(no email)'}</span>
            <span className="text-gray-500"> → {userResolved._id.slice(0, 8)}…</span>
          </span>
        ) : (
          <span className="text-amber-400">no match — filter ignored</span>
        )}
      </span>,
    );
  }
  return (
    <div className="text-[11px] text-gray-500 flex items-center gap-4 flex-wrap">
      {parts.map((p, i) => (
        <span key={i}>{p}</span>
      ))}
    </div>
  );
}

// Hardcoded in alphy-reflect-web/helpers/fetchWithTimeout.js lines 99–101.
// Mirrored here for the tuning guidance — if those values change in the
// source repo, bump these.
const CURRENT_HEDGE_DELAY_1_MS = 600;
const CURRENT_HEDGE_DELAY_2_MS = 1800;

function roundUpTo(n: number | null | undefined, step: number): number | null {
  if (n === null || n === undefined) return null;
  return Math.max(step, Math.ceil(n / step) * step);
}

interface HedgeTuningSuggestion {
  level: 'healthy' | 'fires-too-often' | 'wasted' | 'tail-escapes' | 'low-volume';
  summary: string;
  suggestedDelay1: number | null;
  suggestedDelay2: number | null;
}

function suggestHedgeTuning(
  row: import('../types').OutlookPerfHedgeByName,
): HedgeTuningSuggestion {
  const fire = row.hedgeFireRate ?? 0;
  const wonByHedge = (row.winnerByAttemptPct[2] ?? 0) + (row.winnerByAttemptPct[3] ?? 0);
  const p95 = row.winnerP95;
  const p99 = row.winnerP99;

  // Suggested delays: delay1 slightly above p95 (to keep fire rate ~5%),
  // delay2 around p99 (or 3× delay1 if p99 is missing).
  const d1 = roundUpTo(p95, 50);
  const d2 = roundUpTo(p99, 100) ?? (d1 ? d1 * 3 : null);

  // Not enough data to recommend confidently.
  if (row.groups < 50) {
    return {
      level: 'low-volume',
      summary: `Only ${row.groups} logical fetches in window — wait for more data before tuning.`,
      suggestedDelay1: null,
      suggestedDelay2: null,
    };
  }

  // p99 winner exceeds the current second delay: some requests fall through
  // all three attempts and still take forever. The hedge can't catch this.
  if (p99 !== null && p99 > CURRENT_HEDGE_DELAY_2_MS) {
    return {
      level: 'tail-escapes',
      summary: `Tail escapes the hedge — winner p99 (${Math.round(p99)}ms) exceeds delay2 (${CURRENT_HEDGE_DELAY_2_MS}ms). Lowering delay2 toward ${d2 ?? '?'}ms catches more of the tail; investigating the upstream is usually the bigger lever.`,
      suggestedDelay1: d1,
      suggestedDelay2: d2,
    };
  }

  // Fires too often: doubling load without proportional benefit.
  if (fire > 0.15) {
    return {
      level: 'fires-too-often',
      summary: `Hedges fire ${(fire * 100).toFixed(1)}% of the time — well above the 5% target. Raise delay1 to ~${d1 ?? '?'}ms to bring fire rate down (current 600ms is below the typical request).`,
      suggestedDelay1: d1,
      suggestedDelay2: d2,
    };
  }

  // Fires often enough but loses most races — pure waste.
  if (fire > 0.05 && wonByHedge < 0.10) {
    return {
      level: 'wasted',
      summary: `Hedges fire ${(fire * 100).toFixed(1)}% of the time but only win ${(wonByHedge * 100).toFixed(1)}% of those races. Mostly wasted load. Raise delay1 toward p99 (~${d2 ?? '?'}ms).`,
      suggestedDelay1: d1,
      suggestedDelay2: d2,
    };
  }

  // Otherwise: healthy.
  return {
    level: 'healthy',
    summary: `Healthy — fire rate ${(fire * 100).toFixed(1)}%, ${(wonByHedge * 100).toFixed(1)}% of races won by a hedge. Current 600/1800 delays sit well above winner p95 (${p95 !== null ? Math.round(p95) + 'ms' : '—'}).`,
    suggestedDelay1: d1,
    suggestedDelay2: d2,
  };
}

function HedgeTuningTable({
  data,
  error,
  isLoading,
}: {
  data: import('../types').OutlookPerfHedgeResponse | null;
  error?: unknown;
  isLoading?: boolean;
}) {
  if (error && !data) return <ErrorBanner error={error} />;
  if (!data) return <Text className="text-gray-500 text-sm">{isLoading ? 'Loading…' : 'No data.'}</Text>;
  if (data.byName.length === 0) {
    return <Text className="text-gray-500 text-sm">No fetches in this range.</Text>;
  }

  const LEVEL_COLOR: Record<HedgeTuningSuggestion['level'], string> = {
    healthy: 'text-emerald-400',
    'fires-too-often': 'text-amber-400',
    wasted: 'text-amber-400',
    'tail-escapes': 'text-rose-400',
    'low-volume': 'text-gray-500',
  };
  const LEVEL_LABEL: Record<HedgeTuningSuggestion['level'], string> = {
    healthy: 'Healthy',
    'fires-too-often': 'Fires too often',
    wasted: 'Wasted',
    'tail-escapes': 'Tail escapes',
    'low-volume': 'Low volume',
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-gray-500 text-xs">
          <tr>
            <th className="text-left py-1 pr-3" title="FetchLog.name — the upstream identifier.">Name</th>
            <th className="text-right py-1 px-3" title="Logical fetches — one per (reflection, classifier) tuple. Each may spawn 1, 2, or 3 attempts.">Logical fetches</th>
            <th className="text-right py-1 px-3" title="p95 of the WINNING attempt only — user-perceived latency. The basis for the suggested first delay.">winner p95</th>
            <th className="text-right py-1 px-3" title="p99 of the winning attempt only — the bad tail. Basis for the suggested second delay.">winner p99</th>
            <th className="text-right py-1 px-3" title="% of logical fetches where the hedge fired. Target ~5% per Tail-at-Scale.">Fire %</th>
            <th className="text-right py-1 px-3" title="Currently hardcoded as the +600ms and +1800ms constants in alphy-reflect-web/helpers/fetchWithTimeout.js (delay before attempts #2 and #3 fire).">Current delays</th>
            <th className="text-right py-1 px-3" title="Suggested first delay (round-up of winner p95 to nearest 50ms) and second delay (round-up of winner p99 to nearest 100ms).">Suggested</th>
            <th className="text-left py-1 pl-3" title="Rule-based assessment of the current tuning vs the observed latency distribution. Hover the verdict text for the reasoning.">Verdict</th>
          </tr>
        </thead>
        <tbody className="text-gray-200">
          {data.byName.map((r) => {
            const s = suggestHedgeTuning(r);
            return (
              <tr key={r.name} className="border-t border-gray-800 align-top">
                <td className="py-1 pr-3 font-mono text-xs">{r.name}</td>
                <td className="py-1 px-3 text-right">{fmtNumber(r.groups)}</td>
                <td className="py-1 px-3 text-right">{fmtMs(r.winnerP95)}</td>
                <td className="py-1 px-3 text-right">{fmtMs(r.winnerP99)}</td>
                <td className="py-1 px-3 text-right">{fmtPercent(r.hedgeFireRate)}</td>
                <td className="py-1 px-3 text-right font-mono text-xs text-gray-400">
                  {CURRENT_HEDGE_DELAY_1_MS} / {CURRENT_HEDGE_DELAY_2_MS}
                </td>
                <td className="py-1 px-3 text-right font-mono text-xs">
                  {s.suggestedDelay1 !== null
                    ? `${s.suggestedDelay1} / ${s.suggestedDelay2 ?? '?'}`
                    : '—'}
                </td>
                <td className="py-1 pl-3">
                  <div className={`text-xs font-semibold ${LEVEL_COLOR[s.level]}`}>
                    {LEVEL_LABEL[s.level]}
                  </div>
                  <div className="text-gray-400 text-[11px] mt-0.5 max-w-md">{s.summary}</div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function HedgeSummary({
  data,
  error,
  isLoading,
}: {
  data: import('../types').OutlookPerfHedgeResponse | null;
  error?: unknown;
  isLoading?: boolean;
}) {
  if (error && !data) return <ErrorBanner error={error} />;
  if (!data) return <Text className="text-gray-500 text-sm">{isLoading ? 'Loading…' : 'No data.'}</Text>;
  if (!data.groups) return <Text className="text-gray-500 text-sm">No fetches in this range.</Text>;

  const wonByPct = data.winnerByAttemptPct;
  const s = data.savings;

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi
          label="Hedge fire rate"
          value={fmtPercent(data.hedgeFireRate)}
          hint={`${fmtNumber(data.groups)} logical fetches · avg ${data.avgAttemptsPerGroup?.toFixed(2) ?? '—'} attempts`}
        />
        <Kpi
          label="Won by attempt #1"
          value={fmtPercent(wonByPct[1])}
          hint={`${fmtNumber(data.winnerByAttempt[1])} groups · first try good enough`}
        />
        <Kpi
          label="Won by hedge (#2 or #3)"
          value={fmtPercent((wonByPct[2] ?? 0) + (wonByPct[3] ?? 0))}
          hint={`#2 ${fmtPercent(wonByPct[2])} · #3 ${fmtPercent(wonByPct[3])}`}
        />
        <Kpi
          label="Avg wall time saved by hedge"
          value={fmtMs(s.avgSavedMs)}
          hint={
            s.sampleSize
              ? `p50 ${fmtMs(s.p50SavedMs)} · p95 ${fmtMs(s.p95SavedMs)} · helped ${fmtPercent(s.hedgeWinsThatActuallyHelpedRate)} of the time`
              : 'no hedge wins yet'
          }
        />
      </div>
      <Text className="text-gray-500 text-xs">
        <b>How "saved" is estimated:</b> when attempt #2 or #3 wins and attempt #1 also completed
        (just later), we compare attempt #1's full responseMs to the winner's start delay
        (+600ms or +1800ms) plus its responseMs. Positive = hedging actually beat what the user
        would've waited for; negative = attempt #1 would've finished first anyway (the hedge
        completion just happened to come back first by chance).
      </Text>
    </div>
  );
}

function RequestDetailModal({
  apiRequestId,
  onClose,
}: {
  apiRequestId: string | null;
  onClose: () => void;
}) {
  const detail = useOutlookPerfRequestDetail(apiRequestId);

  return (
    <Dialog open={apiRequestId !== null} onClose={onClose} static={true}>
      <DialogPanel className="max-w-5xl bg-gray-900 ring-1 ring-gray-800">
        <Flex justifyContent="between" alignItems="center" className="mb-3">
          <Title className="text-gray-100">ApiRequest detail</Title>
          <button
            type="button"
            onClick={onClose}
            className="text-xs px-2 py-1 rounded ring-1 ring-gray-700 text-gray-300 hover:text-white hover:bg-gray-800"
          >
            Close
          </button>
        </Flex>
        {detail.isLoading && !detail.data && (
          <Text className="text-gray-500 text-sm">Loading…</Text>
        )}
        {detail.error && (
          <Text className="text-rose-400 text-sm">
            {detail.error instanceof Error ? detail.error.message : 'Failed to load'}
          </Text>
        )}
        {detail.data && <RequestDetailBody data={detail.data} />}
      </DialogPanel>
    </Dialog>
  );
}

function RequestDetailBody({ data }: { data: OutlookPerfRequestDetail }) {
  const { apiRequest: a, totals: t, reflections, fetchGroups } = data;

  return (
    <div className="space-y-4">
      <div className="text-xs text-gray-400 space-y-1 font-mono">
        <div>id: <span className="text-gray-200">{a._id}</span></div>
        <div>endpoint: <span className="text-gray-200">{a.endpoint ?? '—'}</span></div>
        <div>at: <span className="text-gray-200">{a.at ? new Date(a.at).toLocaleString() : '—'}</span></div>
        <div>org: <span className="text-gray-200">{a.orgId ?? '—'}</span> · user: <span className="text-gray-200">{a.userId ?? '—'}</span></div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="End-to-end" value={fmtMs(a.responseMs)} hint="ApiRequest.responseMs" />
        <Kpi label="Client lag" value={fmtMs(a.requestLag)} hint="browser → server" />
        <Kpi label="Body length" value={fmtNumber(a.bodyLength)} hint="chars" />
        <Kpi label="Fan-out" value={`${fmtNumber(t.sentenceCount)} / ${fmtNumber(t.fetchCount)}`} hint="sentences / upstream calls" />
        <Kpi label="Reflection total" value={fmtMs(t.reflectionTotalMs)} hint="sum across sentences" />
        <Kpi label="Reflection max" value={fmtMs(t.reflectionMaxMs)} hint="slowest sentence" />
        <Kpi label="Fetch sum" value={fmtMs(t.fetchSumMs)} hint="all attempts (overcounts hedges)" />
        <Kpi label="Fetch max" value={fmtMs(t.fetchMaxMs)} hint="slowest single fetch" />
      </div>

      <div>
        <Text className="text-gray-200 text-sm font-semibold mb-1">
          ReflectionLogs ({reflections.length})
        </Text>
        <Text className="text-gray-500 text-xs mb-2">
          One row per sentence classified. Per-classifier columns are wall times in ms (null if
          that classifier didn't run on this sentence).
        </Text>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left py-1 pr-2" title="ReflectionLog.apiSource — which handler in alphy-reflect-web wrote this row (showTextResponse vs getResultForSentences).">apiSource</th>
                <th className="text-left py-1 px-2" title="ReflectionLog.label — final classifier decision for this sentence (e.g. 'disrespect', 'neutral').">label</th>
                <th className="text-right py-1 px-2" title="ReflectionLog.responseMs — total wall time to classify this one sentence.">responseMs</th>
                <th className="text-right py-1 px-2" title="ReflectionLog.'openai-hatesanpre' — wall time for the hate/sanity/preconception classifier on this sentence. Null if it didn't run.">hatesanpre</th>
                <th className="text-right py-1 px-2" title="ReflectionLog.'openai-sentiment' — wall time for the sentiment classifier. Null if it didn't run.">sentiment</th>
                <th className="text-right py-1 px-2" title="ReflectionLog.ner — wall time for named-entity-recognition. Null if it didn't run.">ner</th>
                <th className="text-right py-1 px-2" title="ReflectionLog.intimidation — wall time for the intimidation classifier. Null if it didn't run.">intimidation</th>
                <th className="text-right py-1 px-2" title="ReflectionLog.spacy — wall time for spacy NLP processing. Null if it didn't run.">spacy</th>
                <th className="text-left py-1 pl-2" title="ReflectionLog._id (last 6 chars). Use to grep alphy-reflect-web logs for the same sentence.">_id</th>
              </tr>
            </thead>
            <tbody className="text-gray-200">
              {reflections.map((r) => (
                <tr key={r._id} className="border-t border-gray-800">
                  <td className="py-1 pr-2 font-mono">{r.apiSource ?? '—'}</td>
                  <td className="py-1 px-2">{r.label ?? '—'}</td>
                  <td className="py-1 px-2 text-right font-semibold">{fmtMs(r.responseMs)}</td>
                  <td className="py-1 px-2 text-right">{fmtMs(r.classifiers['openai-hatesanpre'])}</td>
                  <td className="py-1 px-2 text-right">{fmtMs(r.classifiers['openai-sentiment'])}</td>
                  <td className="py-1 px-2 text-right">{fmtMs(r.classifiers.ner)}</td>
                  <td className="py-1 px-2 text-right">{fmtMs(r.classifiers.intimidation)}</td>
                  <td className="py-1 px-2 text-right">{fmtMs(r.classifiers.spacy)}</td>
                  <td className="py-1 pl-2 font-mono text-[10px] text-gray-500">{r._id.slice(-6)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <Text className="text-gray-200 text-sm font-semibold mb-1">
          Fetch groups ({fetchGroups.length})
        </Text>
        <Text className="text-gray-500 text-xs mb-2">
          One group per (reflection, classifier). Multiple rows = the hedge fired; the row marked{' '}
          <span className="text-emerald-400">★ winner</span> is the one that came back first and
          drove the user-facing latency. The others were in-flight when the race resolved and
          still completed (they're not cancelled).
        </Text>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-500">
              <tr>
                <th className="text-left py-1 pr-2" title="FetchLog.name. Shown once per group; '↳' rows are additional attempts in the same logical fetch.">name</th>
                <th className="text-right py-1 px-2" title="Attempt count in this logical fetch group — 1 means the hedge never fired; 2 or 3 means it did.">#</th>
                <th className="text-right py-1 px-2" title="FetchLog.attempt — which attempt number this row is (1, 2, or 3).">attempt</th>
                <th className="text-right py-1 px-2" title="FetchLog.timeout — the delay (ms) before this attempt fired. 0 for attempt #1, 600 for #2, 1800 for #3 (current config).">delay</th>
                <th className="text-right py-1 px-2" title="FetchLog.responseMs — duration of this individual attempt's fetch.">responseMs</th>
                <th className="text-right py-1 px-2" title="HTTP response code returned by the upstream for this attempt. 200 = OK; 4xx/5xx = error.">code</th>
                <th className="text-left py-1 pl-2" title="FetchLog.at (createdAt) — moment the row was saved, which is right after this attempt's fetch resolved. Earliest 'at' across the group wins.">at</th>
                <th className="text-left py-1 pl-2" title="Marked '★ winner' on the attempt that resolved first — the one whose completion the user actually waited for. The others kept running and wrote their FetchLog row anyway.">winner</th>
              </tr>
            </thead>
            <tbody className="text-gray-200">
              {fetchGroups.flatMap((g) =>
                g.attempts.map((a, i) => (
                  <tr
                    key={`${g.reflectionLogId ?? 'none'}-${g.name}-${i}`}
                    className={`border-t border-gray-800 ${a.isWinner ? 'bg-emerald-900/10' : ''}`}
                  >
                    <td className="py-1 pr-2 font-mono">
                      {i === 0 ? g.name : <span className="text-gray-500">↳</span>}
                    </td>
                    <td className="py-1 px-2 text-right text-gray-500">
                      {i === 0 ? g.attemptCount : ''}
                    </td>
                    <td className="py-1 px-2 text-right">{a.attempt ?? '—'}</td>
                    <td className="py-1 px-2 text-right">{a.timeout ? `+${a.timeout}ms` : '—'}</td>
                    <td className="py-1 px-2 text-right">{fmtMs(a.responseMs)}</td>
                    <td className="py-1 px-2 text-right">{a.code ?? '—'}</td>
                    <td className="py-1 pl-2 font-mono text-[10px] text-gray-500">
                      {a.at ? new Date(a.at).toLocaleTimeString() : '—'}
                    </td>
                    <td className="py-1 pl-2">
                      {a.isWinner ? <span className="text-emerald-400">★ winner</span> : ''}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PerRequestTable({
  data,
  error,
  isLoading,
}: {
  data: import('../types').OutlookPerfPerRequestResponse | null;
  error?: unknown;
  isLoading?: boolean;
}) {
  if (error && !data) return <ErrorBanner error={error} />;
  if (!data) {
    return <Text className="text-gray-500 text-sm">{isLoading ? 'Loading…' : 'No data.'}</Text>;
  }
  if (!data.requests) {
    return <Text className="text-gray-500 text-sm">No requests in this range.</Text>;
  }
  const rows: Array<{ label: string; hint: string; row: import('../types').OutlookPerfPctl }> = [
    {
      label: 'End-to-end (ApiRequest)',
      hint: 'what the user feels',
      row: data.apiRequest,
    },
    {
      label: 'Reflection total / request',
      hint: 'sum of all sentence-classification times per request',
      row: data.reflectionTotalPerRequest,
    },
    {
      label: 'Reflection max / request',
      hint: 'slowest single sentence in the request',
      row: data.reflectionMaxPerRequest,
    },
    {
      label: 'Fetch sum / request',
      hint: 'total upstream work — overcounts hedges',
      row: data.fetchSumPerRequest,
    },
    {
      label: 'Fetch max / request',
      hint: 'slowest single upstream call',
      row: data.fetchMaxPerRequest,
    },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-gray-500 text-xs">
          <tr>
            <th className="text-left py-1 pr-3" title="Metric name — each row is a value rolled up PER ApiRequest (so percentiles compare apples-to-apples across the request unit).">Per request</th>
            <th className="text-right py-1 px-3" title="50th percentile of this per-request metric across the window.">p50</th>
            <th className="text-right py-1 px-3" title="95th percentile — the user-facing tail metric.">p95</th>
            <th className="text-right py-1 px-3" title="99th percentile — the worst-case tail.">p99</th>
          </tr>
        </thead>
        <tbody className="text-gray-200">
          {rows.map((r) => (
            <tr key={r.label} className="border-t border-gray-800">
              <td className="py-1 pr-3">
                <div>{r.label}</div>
                <div className="text-gray-500 text-[11px]">{r.hint}</div>
              </td>
              <td className="py-1 px-3 text-right">{fmtMs(r.row.p50)}</td>
              <td className="py-1 px-3 text-right">{fmtMs(r.row.p95)}</td>
              <td className="py-1 px-3 text-right">{fmtMs(r.row.p99)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Text className="text-gray-500 text-xs mt-2">
        n = {fmtNumber(data.requests)} requests
      </Text>
    </div>
  );
}

function VolumeSubChart({
  label,
  data,
  series,
}: {
  label: string;
  data: Array<Record<string, unknown>>;
  series: Parameters<typeof RangeSelectChart>[0]['series'];
}) {
  return (
    <div>
      <Text className="text-gray-400 text-xs mb-1">{label}</Text>
      <RangeSelectChart
        data={data}
        xKey="bucket"
        series={series}
        valueFormatter={fmtNumber}
        syncId="outlook-perf-ts"
        heightClass="h-28"
        hideLegend
      />
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <Card className="bg-gray-900 ring-gray-800 p-4">
      <Text className="text-gray-400 text-xs">{label}</Text>
      <p className="text-2xl font-semibold text-gray-100 mt-1">{value}</p>
      <Text className="text-gray-500 text-xs mt-1">{hint}</Text>
    </Card>
  );
}

function NotConfiguredCard() {
  return (
    <Card className="bg-gray-900 ring-amber-700">
      <Title className="text-amber-200">Reflect Mongo not configured</Title>
      <Text className="text-gray-400 text-sm mt-2">
        Set <span className="font-mono">LEGACY_REFLECT_MONGODB_URI</span> on the dashboard process
        to enable this page. It must point at the alphy-reflect-web database (the same Mongo cluster
        is fine — just a different db name). The connection is opened read-only.
      </Text>
    </Card>
  );
}

function isNotConfiguredError(err: unknown): boolean {
  if (!err) return false;
  // fetchJson throws Error(error.error.message); the route returns a message
  // starting with this exact phrase for the 503 not-configured case.
  const msg = err instanceof Error ? err.message : String(err);
  return msg.includes('LEGACY_REFLECT_MONGODB_URI is not configured');
}
